import {
    CalculatorPresentationContractError,
    requirePresentationArray,
    requirePresentationFields,
    requirePresentationFinite,
    requirePresentationRecord,
    requirePresentationString,
} from "./calculator-presentation-contract.js";
import {
    calculatorDataEnumLabel,
    calculatorDataText,
} from "./calculator-data-i18n.js";
import { parseCalculatorClaimDateV1 } from "../contracts/calculator-claim-date-v1.js";
import { isCanonicalCalculatorCreatedAtUtc } from "../calculator-user-layer-v4-values.js";
import { presentCalculatorClaimDateV1 } from "./calculator-claim-date-presentation.js";
import {
    provenanceClaimsText,
    typedProvenanceIndicatorStyle,
} from "./provenance-indicator.js";

const TRACE_STATUSES = new Set([
    "effective",
    "overridden",
    "refuted",
    "inactiveCondition",
]);
const OBSERVATION_SCOPES = new Set([
    "rankingFullyContained",
    "rankingPartialIntersection",
]);
const OPERATION_CONFIDENCE_VALUES = new Set(["known", "guessed"]);

function requireRecord(value, path) {
    return requirePresentationRecord(value, path);
}

function requireString(record, key, path) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
        throw new CalculatorPresentationContractError(`${path}.${key}`, "missing field");
    }
    return requirePresentationString(record[key], `${path}.${key}`);
}

function parseEvidencePresentation(table, evidenceClass) {
    const path = `presentation.provenance.evidenceClasses.${evidenceClass}`;
    if (!Object.prototype.hasOwnProperty.call(table, evidenceClass)) {
        throw new CalculatorPresentationContractError(
            "presentation.provenance.evidenceClasses",
            `missing evidence class ${evidenceClass}`,
        );
    }
    const value = requirePresentationFields(
        table[evidenceClass],
        ["color", "icon", "label", "sortRank", "tone"],
        path,
    );
    return {
        label: requirePresentationString(value.label, `${path}.label`),
        color: requirePresentationString(value.color, `${path}.color`),
        icon: requirePresentationString(value.icon, `${path}.icon`, { allowEmpty: true }),
        tone: requirePresentationString(value.tone, `${path}.tone`),
        sortRank: requirePresentationFinite(value.sortRank, `${path}.sortRank`, {
            minimum: 0,
        }),
    };
}

function operationObservationScope(operation, path) {
    if (
        !Object.prototype.hasOwnProperty.call(operation, "provenance")
        || operation.provenance === null
    ) {
        return null;
    }
    const provenance = requireRecord(operation.provenance, `${path}.provenance`);
    if (
        !Object.prototype.hasOwnProperty.call(provenance, "observationScope")
        || provenance.observationScope === null
    ) {
        return null;
    }
    const scope = requirePresentationString(
        provenance.observationScope,
        `${path}.provenance.observationScope`,
    );
    if (!OBSERVATION_SCOPES.has(scope)) {
        throw new CalculatorPresentationContractError(
            `${path}.provenance.observationScope`,
            "unsupported observation scope",
        );
    }
    return scope;
}

function parseStatusLabel(table, status) {
    if (!TRACE_STATUSES.has(status)) {
        throw new CalculatorPresentationContractError(
            "projection.provenanceTraces.status",
            "unsupported status",
        );
    }
    const path = `presentation.provenance.statuses.${status}`;
    if (!Object.prototype.hasOwnProperty.call(table, status)) {
        throw new CalculatorPresentationContractError(
            "presentation.provenance.statuses",
            `missing status ${status}`,
        );
    }
    return requirePresentationString(table[status], path);
}

export function calculatorProvenanceFactKindLabel(table, factKind) {
    if (!Object.prototype.hasOwnProperty.call(table, factKind)) {
        throw new CalculatorPresentationContractError(
            "presentation.provenance.factKinds",
            `missing fact kind ${factKind}`,
        );
    }
    return requirePresentationString(
        table[factKind],
        `presentation.provenance.factKinds.${factKind}`,
    );
}

function optionalProvenanceString(provenance, key, path) {
    if (
        !Object.prototype.hasOwnProperty.call(provenance, key)
        || provenance[key] === null
        || provenance[key] === undefined
    ) {
        return null;
    }
    return requirePresentationString(provenance[key], `${path}.${key}`);
}

function optionalProvenanceInteger(provenance, key, path, { minimum = 0 } = {}) {
    if (
        !Object.prototype.hasOwnProperty.call(provenance, key)
        || provenance[key] === null
        || provenance[key] === undefined
    ) {
        return null;
    }
    const value = requirePresentationFinite(provenance[key], `${path}.${key}`, { minimum });
    if (!Number.isInteger(value)) {
        throw new CalculatorPresentationContractError(`${path}.${key}`, "expected an integer");
    }
    return value;
}

function optionalProvenanceHash(provenance, path) {
    if (
        !Object.prototype.hasOwnProperty.call(provenance, "contentHash")
        || provenance.contentHash === null
        || provenance.contentHash === undefined
    ) {
        return null;
    }
    const value = requirePresentationFields(
        provenance.contentHash,
        ["algorithm", "digest"],
        `${path}.contentHash`,
    );
    const algorithm = requirePresentationString(value.algorithm, `${path}.contentHash.algorithm`);
    const digest = requirePresentationString(value.digest, `${path}.contentHash.digest`);
    return `${algorithm}:${digest}`;
}

function optionalOperationConfidence(provenance, path) {
    const confidence = optionalProvenanceString(provenance, "confidence", path);
    if (confidence !== null && !OPERATION_CONFIDENCE_VALUES.has(confidence)) {
        throw new CalculatorPresentationContractError(
            `${path}.confidence`,
            "expected known or guessed",
        );
    }
    return confidence;
}

function optionalOperationAuthor(provenance, path) {
    if (
        !Object.prototype.hasOwnProperty.call(provenance, "author")
        || provenance.author === null
        || provenance.author === undefined
    ) {
        return null;
    }
    const author = requirePresentationFields(
        provenance.author,
        ["displayName", "handle", "id"],
        `${path}.author`,
    );
    const authorField = (field) => {
        const value = requirePresentationString(
            author[field],
            `${path}.author.${field}`,
        );
        if (value !== value.trim()) {
            throw new CalculatorPresentationContractError(
                `${path}.author.${field}`,
                "must not contain surrounding whitespace",
            );
        }
        return value;
    };
    return Object.freeze({
        id: authorField("id"),
        displayName: authorField("displayName"),
        handle: authorField("handle"),
    });
}

function optionalCreatedAtUtc(provenance, path) {
    const createdAtUtc = optionalProvenanceString(provenance, "createdAtUtc", path);
    if (
        createdAtUtc !== null
        && !isCanonicalCalculatorCreatedAtUtc(createdAtUtc)
    ) {
        throw new CalculatorPresentationContractError(
            `${path}.createdAtUtc`,
            "expected a valid canonical YYYY-MM-DDTHH:mm:ss.sssZ timestamp",
        );
    }
    return createdAtUtc;
}

function optionalClaimDate(provenance, path) {
    if (
        !Object.prototype.hasOwnProperty.call(provenance, "claimDate")
        || provenance.claimDate === null
        || provenance.claimDate === undefined
    ) {
        return null;
    }
    try {
        return parseCalculatorClaimDateV1(provenance.claimDate, `${path}.claimDate`);
    } catch (error) {
        throw new CalculatorPresentationContractError(
            error.path ?? `${path}.claimDate`,
            error.message,
        );
    }
}

function provenanceCardRow(labelKey, labelFallback, value, { icon = "", tone = "" } = {}) {
    if (value === null || value === undefined || String(value).trim() === "") {
        return null;
    }
    return Object.freeze({
        label: calculatorDataText(labelKey, labelFallback),
        value: String(value),
        icon,
        tone,
    });
}

function observationScopePresentation(scope) {
    if (scope === "rankingFullyContained") {
        return Object.freeze({
            label: calculatorDataText(
                "calculator.data.evidence.scope.fully_contained",
                "Fully contained ranking rings",
            ),
            icon: "ring-full",
            tone: "ranking-full",
        });
    }
    if (scope === "rankingPartialIntersection") {
        return Object.freeze({
            label: calculatorDataText(
                "calculator.data.evidence.scope.partial_intersection",
                "Boundary-intersecting ranking rings",
            ),
            icon: "ring-partial",
            tone: "ranking-partial",
        });
    }
    return null;
}

function operationProvenancePresentation(operation, path) {
    if (
        !Object.prototype.hasOwnProperty.call(operation, "provenance")
        || operation.provenance === null
    ) {
        throw new CalculatorPresentationContractError(
            `${path}.provenance`,
            "missing operation provenance",
        );
    }
    const provenancePath = `${path}.provenance`;
    const provenance = requireRecord(operation.provenance, provenancePath);
    const sourceId = requireString(provenance, "sourceId", provenancePath);
    const sourceName = optionalProvenanceString(provenance, "sourceName", provenancePath);
    const observationScope = operationObservationScope(operation, path);
    const observationCount = optionalProvenanceInteger(
        provenance,
        "observationCount",
        provenancePath,
        { minimum: 1 },
    );
    if ((observationScope === null) !== (observationCount === null)) {
        throw new CalculatorPresentationContractError(
            provenancePath,
            "observationScope and observationCount must be present together",
        );
    }
    const confidence = optionalOperationConfidence(provenance, provenancePath);
    const author = optionalOperationAuthor(provenance, provenancePath);
    const createdAtUtc = optionalCreatedAtUtc(provenance, provenancePath);
    const claimDate = optionalClaimDate(provenance, provenancePath);
    if ((author === null) !== (createdAtUtc === null)) {
        throw new CalculatorPresentationContractError(
            provenancePath,
            "author and createdAtUtc must either both be absent or both be present",
        );
    }
    return Object.freeze({
        sourceId,
        comment: optionalProvenanceString(provenance, "comment", provenancePath),
        confidence,
        author,
        createdAtUtc,
        claimDate,
        sourceName,
        sourceRevision: optionalProvenanceString(provenance, "sourceRevision", provenancePath),
        sourceTable: optionalProvenanceString(provenance, "sourceTable", provenancePath),
        sourceSheet: optionalProvenanceString(provenance, "sourceSheet", provenancePath),
        sourceRow: optionalProvenanceInteger(provenance, "sourceRow", provenancePath),
        contentHash: optionalProvenanceHash(provenance, provenancePath),
        importedAtUtc: optionalProvenanceString(provenance, "importedAtUtc", provenancePath),
        observedAtUtc: optionalProvenanceString(provenance, "observedAtUtc", provenancePath),
        observationScope,
        observationCount,
    });
}

function deactivatorRows(deactivators, path) {
    return requirePresentationArray(deactivators, path).flatMap((entry, index) => {
        const entryPath = `${path}[${index}]`;
        const value = requireRecord(entry, entryPath);
        const layer = requireRecord(value.layer, `${entryPath}.layer`);
        const operation = requireRecord(value.operation, `${entryPath}.operation`);
        const kind = requireString(operation, "kind", `${entryPath}.operation`);
        if (kind !== "refuteEvidence") {
            throw new CalculatorPresentationContractError(
                `${entryPath}.operation.kind`,
                "expected refuteEvidence",
            );
        }
        const provenance = operationProvenancePresentation(
            operation,
            `${entryPath}.operation`,
        );
        const source = provenance.sourceName
            ?? requireString(layer, "name", `${entryPath}.layer`);
        return [
            provenanceCardRow(
                "calculator.data.metadata.refuted_by",
                "Refuted by",
                source,
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_reason",
                "Refutation reason",
                requireString(operation, "reasonCode", `${entryPath}.operation`),
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_comment",
                "Refutation comment",
                provenance.comment,
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_confidence",
                "Refutation confidence",
                provenance.confidence === null
                    ? null
                    : calculatorDataEnumLabel(provenance.confidence),
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_author",
                "Refutation author",
                provenance.author?.displayName,
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_author_handle",
                "Refutation author handle",
                provenance.author?.handle,
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_author_id",
                "Refutation author ID",
                provenance.author?.id,
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_created",
                "Refutation created",
                provenance.createdAtUtc,
                { icon: "date-confirmed" },
            ),
            provenanceCardRow(
                "calculator.data.metadata.refutation_claim_date",
                "Refutation claim date",
                presentCalculatorClaimDateV1(provenance.claimDate),
                { icon: "date-confirmed" },
            ),
        ].filter(Boolean);
    });
}

function candidateDetailCard({
    deactivators,
    evidence,
    evidenceClass,
    factKey,
    factLabel,
    factPresenter,
    layer,
    operation,
    operationPath,
    provenance,
    statusLabel,
    style,
}) {
    const sourceLabel = provenance.sourceName
        ?? (evidenceClass === "user" ? layer.name : provenance.sourceId);
    const observation = observationScopePresentation(provenance.observationScope);
    const rows = [
        ...factPresenter.rows(factKey, operation, operationPath),
        provenanceCardRow(
            "calculator.data.metadata.comment",
            "Comment",
            provenance.comment,
        ),
        provenanceCardRow(
            "calculator.data.metadata.confidence",
            "Confidence",
            provenance.confidence === null
                ? null
                : calculatorDataEnumLabel(provenance.confidence),
        ),
        provenanceCardRow(
            "calculator.data.metadata.author",
            "Author",
            provenance.author?.displayName,
        ),
        provenanceCardRow(
            "calculator.data.metadata.author_handle",
            "Author handle",
            provenance.author?.handle,
        ),
        provenanceCardRow(
            "calculator.data.metadata.author_id",
            "Author ID",
            provenance.author?.id,
        ),
        provenanceCardRow(
            "calculator.data.metadata.created",
            "Created",
            provenance.createdAtUtc,
            { icon: "date-confirmed" },
        ),
        provenanceCardRow(
            "calculator.data.metadata.claim_date",
            "Claim date",
            presentCalculatorClaimDateV1(provenance.claimDate),
            { icon: "date-confirmed" },
        ),
        sourceLabel !== provenance.sourceId
            ? provenanceCardRow(
                "calculator.data.metadata.source_id",
                "Source ID",
                provenance.sourceId,
            )
            : null,
        provenanceCardRow(
            "calculator.data.metadata.evidence_class",
            "Evidence class",
            evidence.label,
        ),
        provenanceCardRow(
            "calculator.data.metadata.layer",
            "Layer",
            layer.name,
        ),
        provenanceCardRow(
            "calculator.data.metadata.source_revision",
            "Source revision",
            provenance.sourceRevision,
        ),
        provenanceCardRow(
            "calculator.data.metadata.table",
            "Table",
            provenance.sourceTable,
        ),
        provenanceCardRow(
            "calculator.data.metadata.sheet",
            "Sheet",
            provenance.sourceSheet,
        ),
        provenanceCardRow(
            "calculator.data.metadata.row",
            "Row",
            provenance.sourceRow,
        ),
        provenanceCardRow(
            "calculator.data.metadata.content_hash",
            "Content hash",
            provenance.contentHash,
        ),
        provenance.claimDate === null
            ? provenanceCardRow(
                "calculator.data.metadata.imported",
                "Imported",
                provenance.importedAtUtc,
                { icon: "date-confirmed" },
            )
            : null,
        provenanceCardRow(
            "calculator.data.metadata.observed",
            "Observed",
            provenance.observedAtUtc,
            { icon: "date-confirmed" },
        ),
        observation
            ? provenanceCardRow(
                "calculator.data.metadata.observation_scope",
                "Observation scope",
                observation.label,
                { icon: observation.icon, tone: observation.tone },
            )
            : null,
        provenanceCardRow(
            "calculator.data.metadata.observation_count",
            "Observation count",
            provenance.observationCount,
            observation
                ? { icon: observation.icon, tone: observation.tone }
                : {},
        ),
        ...deactivatorRows(
            deactivators,
            `${operationPath}.deactivators`,
        ),
    ].filter(Boolean);
    return Object.freeze({
        summary: `${factLabel} · ${statusLabel}`,
        badge: sourceLabel,
        icon: style.sourceIcon || evidence.icon,
        tone: style.sourceTone,
        rows: Object.freeze(rows),
    });
}

function optionalLayerString(layer, key, path) {
    if (!Object.prototype.hasOwnProperty.call(layer, key)) {
        return null;
    }
    return requirePresentationString(layer[key], `${path}.${key}`);
}

function optionalLayerOrder(layer, path) {
    if (!Object.prototype.hasOwnProperty.call(layer, "order")) {
        return null;
    }
    const order = requirePresentationFinite(layer.order, `${path}.order`, { minimum: 0 });
    if (!Number.isInteger(order)) {
        throw new CalculatorPresentationContractError(`${path}.order`, "expected an integer");
    }
    return order;
}

function canonicalTypedValue(value) {
    if (Array.isArray(value)) {
        return value.map(canonicalTypedValue);
    }
    if (!value || typeof value !== "object") {
        return value;
    }
    return Object.fromEntries(
        Object.keys(value)
            .sort()
            .filter((key) => value[key] !== undefined)
            .map((key) => [key, canonicalTypedValue(value[key])]),
    );
}

export function projectCalculatorProvenanceCandidate(
    traceId,
    factKey,
    factLabel,
    candidate,
    candidatePath,
    presentation,
    factPresenter,
) {
    const factKeyPath = `${candidatePath}.factKey`;
    const parsedFactKey = requireRecord(factKey, factKeyPath);
    const factKind = requireString(parsedFactKey, "kind", factKeyPath);
    const value = requireRecord(candidate, candidatePath);
    const layer = requireRecord(value.layer, `${candidatePath}.layer`);
    const operation = requireRecord(value.operation, `${candidatePath}.operation`);
    const operationId = requireString(operation, "operationId", `${candidatePath}.operation`);
    const evidenceClass = requireString(layer, "evidenceClass", `${candidatePath}.layer`);
    const status = requireString(value, "status", candidatePath);
    const evidence = parseEvidencePresentation(
        presentation.evidenceClasses,
        evidenceClass,
    );
    const statusLabel = parseStatusLabel(presentation.statuses, status);
    const provenance = operationProvenancePresentation(
        operation,
        `${candidatePath}.operation`,
    );
    const style = typedProvenanceIndicatorStyle(factKind, evidenceClass, {
        active: status === "effective",
        confidence: provenance.confidence,
        layerId: optionalLayerString(layer, "layerId", `${candidatePath}.layer`),
        observationScope: provenance.observationScope,
    });
    const card = candidateDetailCard({
        deactivators: Object.prototype.hasOwnProperty.call(value, "deactivators")
            ? value.deactivators
            : [],
        evidence,
        evidenceClass,
        factKey: parsedFactKey,
        factLabel,
        factPresenter,
        layer,
        operation,
        operationPath: `${candidatePath}.operation`,
        provenance,
        statusLabel,
        style,
    });
    const sourceLabel = provenance.sourceName
        ?? (evidenceClass === "user" ? layer.name : provenance.sourceId);
    const claims = Object.freeze([Object.freeze({
        card,
        laneId: traceId,
        status,
    })]);
    return Object.freeze({
        laneId: traceId,
        operationId,
        status,
        card,
        segment: Object.freeze({
            segmentId: `${traceId}:${operationId}`,
            active: status === "effective",
            color: style.color,
            claims,
            detail: provenanceClaimsText(claims),
            label: factLabel,
            sortRank: evidence.sortRank,
            sourceIcon: style.sourceIcon || evidence.icon,
            sourceKind: evidenceClass,
            sourceLabel,
            sourceTone: style.sourceTone,
        }),
        semanticKey: JSON.stringify(canonicalTypedValue({
            factKind,
            evidenceClass,
            layer: {
                layerId: optionalLayerString(layer, "layerId", `${candidatePath}.layer`),
                order: optionalLayerOrder(layer, `${candidatePath}.layer`),
                kind: optionalLayerString(layer, "kind", `${candidatePath}.layer`),
            },
            sourceId: provenance.sourceId,
            observationScope: provenance.observationScope,
        })),
    });
}

export function calculatorProvenanceClaimEntries(presentations) {
    return Object.freeze(presentations.map((candidate) => (
        Object.freeze({
            card: candidate.card,
            laneId: candidate.laneId,
            status: candidate.status,
        })
    )));
}

export function mergeCalculatorProvenanceClaims(resolutions) {
    return Object.freeze(resolutions.flatMap(
        ({ claimEntries }) => claimEntries,
    ));
}
